import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Crown,
  Loader2,

  CheckCircle,
  RotateCcw,
} from 'lucide-react';


import { useCheckout } from '../hooks/useCheckout';
import { useIAPCheckout } from '../hooks/useIAPCheckout';
import { usePremium } from '../hooks/usePremium';
import { isNativeApp } from '../utils/platform';
import { trackMembershipView } from '../utils/analytics';
import { MembershipBenefitsText } from '../components/MembershipBenefitsText';
import { ANNUAL_MONTHLY_EQUIVALENT, ANNUAL_PRICE, MONTHLY_PRICE } from '../config/pricing';

type SubscriptionOption = {
  id: 'monthly' | 'annual';
  label: string;
  totalPrice: string;
  rightPrice: string;
  rightSubtext: string;
  background: string;
  border: string;
  badge?: string;
  priceId: string;
};

const SUBSCRIPTION_OPTIONS: SubscriptionOption[] = [
  {
    id: 'annual',
    label: 'Annual Membership',
    totalPrice: `Billed ${ANNUAL_PRICE} annually`,
    rightPrice: ANNUAL_MONTHLY_EQUIVALENT,
    rightSubtext: '/month',
    background: 'bg-emerald-500 hover:bg-emerald-600',
    border: 'border-blue-500',
    badge: 'Best value',
    priceId: process.env.REACT_APP_STRIPE_PREMIUM_ANNUAL_PRICE_ID!
  },
  {
    id: 'monthly',
    label: 'Monthly Membership',
    totalPrice: `Billed ${MONTHLY_PRICE} monthly`,
    rightPrice: MONTHLY_PRICE,
    rightSubtext: '/month',
    background: 'bg-amber-500 hover:bg-amber-600',
    border: 'border-amber-600/50',
    priceId: process.env.REACT_APP_STRIPE_PREMIUM_MONTHLY_PRICE_ID!
  }
];

type NativeSubscriptionOption = SubscriptionOption & {
  package: {
    product: {
      priceString: string;
    };
  } | undefined | null;
  purchase: () => void;
};

/**
 * IAP section for native iOS app.
 * Uses RevenueCat purchases directly instead of RevenueCatUI, whose SwiftUI
 * paywall crashes on iOS 17 during layout on physical devices.
 * Also shows Restore Purchases (App Store requirement).
 */
const IAPSubscriptionSection: React.FC<{
  premiumLoading: boolean;
  showMemberOverlay: boolean;
}> = ({ premiumLoading, showMemberOverlay }) => {
  const {
    monthlyPackage,
    annualPackage,
    purchaseMonthly,
    purchaseAnnual,
    restorePurchases,
    loading,
    error,
  } = useIAPCheckout();

  const nativeOptions: NativeSubscriptionOption[] = SUBSCRIPTION_OPTIONS.map((option) => ({
    ...option,
    package: option.id === 'monthly' ? monthlyPackage : annualPackage,
    purchase: option.id === 'monthly' ? purchaseMonthly : purchaseAnnual,
  }));
  const hasAnyPackage = nativeOptions.some((option) => option.package);

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/15 dark:text-red-200">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {nativeOptions.map((option) => {
          const product = option.package?.product;
          const isUnavailable = !product;

          return (
            <div key={option.id} className="relative">
              {option.badge && (
                <span className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 inline-flex items-center rounded-full border border-blue-500 bg-white px-3 py-0.5 text-xs font-semibold uppercase tracking-wide text-blue-600 dark:bg-background">
                  {option.badge}
                </span>
              )}
              <button
                type="button"
                disabled={loading || premiumLoading || showMemberOverlay || isUnavailable}
                onClick={() => void option.purchase()}
                className={`group relative flex w-full flex-col gap-2 rounded-2xl border ${
                  premiumLoading || showMemberOverlay || isUnavailable
                    ? 'border-gray-400/50 bg-gray-400 cursor-not-allowed'
                    : `${option.border} ${option.background} hover:-translate-y-0.5`
                } p-5 text-left text-white transition-transform duration-200 disabled:cursor-not-allowed disabled:opacity-70`}
              >
                {premiumLoading && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/20">
                    <Loader2 className="h-6 w-6 animate-spin text-white" />
                  </div>
                )}
                {showMemberOverlay && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/40">
                    <span className="rounded-full bg-white/90 px-4 py-2 text-center text-sm font-semibold text-gray-700">
                      You're already a member. Thank you for your support! Manage your subscription in Settings.
                    </span>
                  </div>
                )}
                <div className="flex w-full items-center justify-between gap-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-xl font-bold">{option.label}</span>
                    <span className="text-sm text-white/80">{option.totalPrice}</span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-2xl font-bold">{option.rightPrice}</span>
                    <span className="text-sm text-white/80">{option.rightSubtext}</span>
                  </div>
                </div>
                {loading && (
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    <Loader2 className="h-4 w-4 animate-spin" /> Opening purchase sheet...
                  </span>
                )}
                {isUnavailable && !loading && (
                  <span className="text-sm text-white/80">Membership option unavailable</span>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {!hasAnyPackage && !error && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/15 dark:text-amber-200">
          Membership options are still loading. If this does not update, please check your connection and try again.
        </div>
      )}

      {/* Restore Purchases — App Store Review requirement */}
      <div className="flex justify-center">
        <button
          type="button"
          disabled={loading}
          onClick={() => void restorePurchases()}
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <RotateCcw className="h-4 w-4" />
          Restore Purchases
        </button>
      </div>
    </div>
  );
};


export const MembershipPage: React.FC = () => {
  const { startCheckout, loading, error } = useCheckout();
  const { isPremium, loading: premiumLoading } = usePremium();
  const isNative = isNativeApp();
  useEffect(() => {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
    fetch(`${apiBaseUrl}/api/analytics/membership-click`, { method: 'POST' }).catch(() => {});
    trackMembershipView();
  }, []);

  // Only show member overlay after loading completes to prevent flicker
  const showMemberOverlay = !premiumLoading && isPremium;

  return (
    <>
    <Helmet>
      <title>Wallstreetle | Premium Membership</title>
      <meta
        name="description"
        content="Unlock all 9 stock categories with Wallstreetle Premium. Access Financial Services, Consumer Goods, Pharmaceuticals, Energy, Industrial, and Foreign stock puzzles. Daily challenges to boost your financial literacy."
      />
      <link rel="canonical" href="https://wallstreetle.com/membership" />
      <meta property="og:title" content="Wallstreetle | Premium Membership" />
      <meta property="og:description" content="Unlock all 9 stock categories with Wallstreetle Premium. Daily stock puzzles across Financial Services, Consumer Goods, Pharma, Energy, Industrial, and Foreign markets." />
      <meta property="og:url" content="https://wallstreetle.com/membership" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://wallstreetle.com/assets/share-link-image.png" />
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:title" content="Wallstreetle | Premium Membership" />
      <meta property="twitter:description" content="Unlock all 9 stock categories with Wallstreetle Premium. Daily stock puzzles across Financial Services, Consumer Goods, Pharma, Energy, Industrial, and Foreign markets." />
      <meta property="twitter:image" content="https://wallstreetle.com/assets/share-link-image.png" />
    </Helmet>
    <div className="px-4 py-10 sm:py-16">
      <div className="mx-auto w-full max-w-5xl space-y-10">
        <div className="space-y-6">
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="rounded-2xl bg-amber-100 p-3 text-amber-600 dark:bg-amber-500/20">
                <Crown className="h-8 w-8" />
              </div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.3em] text-muted-foreground">
                  Membership
                </p>
                <h1 className="text-3xl font-bold text-foreground sm:text-4xl">
                  Become a Wallstreetle Member
                </h1>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-10">
          <section className="space-y-3">
            {isNative ? (
              <IAPSubscriptionSection
                premiumLoading={premiumLoading}
                showMemberOverlay={showMemberOverlay}
              />
            ) : (
              <div className="space-y-4">
                {error && (
                  <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/15 dark:text-red-200">
                    {error}
                  </div>
                )}
                {SUBSCRIPTION_OPTIONS.map((option) => (
                  <div key={option.id} className="relative">
                    {option.badge && (
                      <span className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 inline-flex items-center rounded-full border border-blue-500 bg-white px-3 py-0.5 text-xs font-semibold uppercase tracking-wide text-blue-600 dark:bg-background">
                        {option.badge}
                      </span>
                    )}
                    <button
                      type="button"
                      disabled={loading || premiumLoading || showMemberOverlay}
                      onClick={() => void startCheckout(option.priceId)}
                      className={`group relative flex w-full flex-col gap-2 rounded-2xl border ${
                        premiumLoading || showMemberOverlay
                          ? 'border-gray-400/50 bg-gray-400 cursor-not-allowed'
                          : `${option.border} ${option.background} hover:-translate-y-0.5`
                      } p-5 text-left text-white transition-transform duration-200 disabled:cursor-not-allowed disabled:opacity-70`}
                    >
                      {premiumLoading && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/20">
                          <Loader2 className="h-6 w-6 animate-spin text-white" />
                        </div>
                      )}
                      {showMemberOverlay && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/40">
                          <span className="rounded-full bg-white/90 px-4 py-2 text-center text-sm font-semibold text-gray-700">
                            You're already a member. Thank you for your support! Manage your subscription in Settings.
                          </span>
                        </div>
                      )}
                      <div className="flex w-full items-center justify-between gap-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-xl font-bold">{option.label}</span>
                          <span className="text-sm text-white/80">{option.totalPrice}</span>
                        </div>
                        <div className="flex flex-col items-end">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl font-bold">{option.rightPrice}</span>
                          </div>
                          <span className="text-sm text-white/80">{option.rightSubtext}</span>
                        </div>
                      </div>
                      {loading && (
                        <span className="flex items-center gap-2 text-sm font-semibold">
                          <Loader2 className="h-4 w-4 animate-spin" /> Redirecting to checkout
                        </span>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-center gap-1.5">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm text-muted-foreground">No commitment, cancel anytime</span>
            </div>
            {!isNative && (
              <div className="flex justify-center">
                <img
                  src="/assets/powered-by-stripe.svg"
                  alt="Powered by Stripe"
                  className="h-8 w-auto"
                  loading="lazy"
                />
              </div>
            )}
          </section>

          <section className="space-y-4">
              <div>
                <h2 className="text-2xl font-semibold text-foreground">Why becoming a member matters</h2>
                <p className="mt-3 text-lg text-foreground">
                  Along with <span className="font-semibold">exclusive benefits</span>, your membership helps keep Wallstreetle <span className="font-semibold">free for everyone</span>, so more people can <span className="font-semibold">learn about the stock market</span> and <span className="font-semibold">build financial confidence</span>.
                </p>
              </div>
          </section>

          <section className="space-y-5">
            <h2 className="text-2xl font-semibold text-foreground">Here's what you get</h2>
            <MembershipBenefitsText size="lg" />
          </section>
        </div>
      </div>
    </div>
    </>
  );
};

export default MembershipPage;
